import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmtErr, fmtDate, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RoleBadge, StatusBadge, PriorityBadge } from "@/components/badges";
import { CompanyStatusBadge } from "@/pages/CompaniesPage";
import { ConfirmDialog } from "@/components/common";
import { toast } from "sonner";
import { Copy, ExternalLink, ArrowLeft, Ban, CheckCircle, Archive, Save, Sparkles, Loader2 } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";

const PORTAL_LABELS = { non_cree: "Non créé", en_attente: "En attente", actif: "Actif", desactive: "Désactivé" };
const AI_STATUS_LABELS = { disabled: "Désactivée", trial: "Essai en cours", active: "Active", expired: "Expirée" };
const AI_STATUS_STYLES = {
  disabled: "bg-neutral-200 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400",
  trial: "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-300",
  active: "bg-green-100 text-green-900 dark:bg-green-950 dark:text-green-300",
  expired: "bg-red-100 text-red-900 dark:bg-red-950 dark:text-red-300",
};

export default function CompanyDetailPage() {
  const { id } = useParams();
  const [company, setCompany] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [tabData, setTabData] = useState({ categories: [], tickets: [], emails: [], logs: [] });
  const [aiNotes, setAiNotes] = useState("");
  const [aiTrialDays, setAiTrialDays] = useState("14");
  const [aiTrialDate, setAiTrialDate] = useState("");
  const [aiBusy, setAiBusy] = useState(false);

  const saveAiOption = async (payload, msg) => {
    setAiBusy(true);
    try {
      await api.patch(`/companies/${id}/ai-option`, payload);
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setAiBusy(false);
    }
  };

  const load = () => {
    api.get(`/companies/${id}`).then(({ data }) => {
      setCompany(data);
      setAiNotes(data.ai_notes || "");
      setForm({
        name: data.name || "", slug: data.slug || "", email: data.email || "", phone: data.phone || "",
        website: data.website || "", address: data.address || "", zip_code: data.zip_code || "",
        city: data.city || "", country: data.country || "", language: data.language || "fr", plan: data.plan || "decouverte",
        siret: data.siret || "", siren: data.siren || "", rcs: data.rcs || "", legal_form: data.legal_form || "",
        vat_number: data.vat_number || "",
        contact_first_name: data.contact?.first_name || "", contact_last_name: data.contact?.last_name || "",
        contact_email: data.contact?.email || "", contact_phone: data.contact?.phone || "", contact_role: data.contact?.role || "",
      });
    }).catch((e) => toast.error(fmtErr(e)));
    api.get("/categories", { params: { company_id: id } }).then(({ data }) => setTabData((p) => ({ ...p, categories: data }))).catch(() => {});
    api.get("/tickets", { params: { company_id: id } }).then(({ data }) => setTabData((p) => ({ ...p, tickets: data }))).catch(() => {});
    api.get("/email-logs", { params: { company_id: id } }).then(({ data }) => setTabData((p) => ({ ...p, emails: data }))).catch(() => {});
    api.get("/audit-logs", { params: { company_id: id } }).then(({ data }) => setTabData((p) => ({ ...p, logs: data }))).catch(() => {});
  };
  useEffect(() => { load(); }, [id]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v === "" ? null : v]));
      await api.patch(`/companies/${id}`, payload);
      toast.success("Société mise à jour");
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (status) => {
    try {
      await api.patch(`/companies/${id}`, { status, suspend_reason: reason || null });
      toast.success(status === "suspended" ? "Société suspendue" : status === "archived" ? "Société archivée" : "Société réactivée");
      setSuspendOpen(false);
      setArchiveOpen(false);
      setReason("");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  if (!company || !form) return <div className="p-8 text-sm text-muted-foreground">Chargement…</div>;

  return (
    <div data-testid="company-detail-page">
      <Link to="/app/companies" data-testid="back-to-companies" className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Entreprises
      </Link>
      <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">{company.name}</h1>
          <p className="mt-1 text-sm text-muted-foreground">Créée le {fmtDate(company.created_at)} · Formule <span className="capitalize">{company.plan}</span></p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <CompanyStatusBadge status={company.status} />
          {company.status !== "active" && (
            <Button data-testid="reactivate-company-button" onClick={() => toggleStatus("active")} className="rounded-sm bg-primary text-white hover:bg-red-700">
              <CheckCircle className="mr-2 h-4 w-4" /> Réactiver
            </Button>
          )}
          {company.status === "active" && (
            <Button data-testid="suspend-company-button" variant="outline" onClick={() => setSuspendOpen(true)} className="rounded-sm border-primary text-primary hover:bg-primary hover:text-white">
              <Ban className="mr-2 h-4 w-4" /> Suspendre
            </Button>
          )}
          {company.status !== "archived" && (
            <Button data-testid="archive-company-button" variant="outline" onClick={() => setArchiveOpen(true)} className="rounded-sm">
              <Archive className="mr-2 h-4 w-4" /> Archiver
            </Button>
          )}
        </div>
      </div>

      {company.status !== "active" && (
        <div data-testid="suspension-banner" className="mb-6 rounded-sm border border-primary/40 bg-[hsl(var(--note-bg))] p-4 text-sm">
          {company.status === "archived" ? "Archivée" : "Suspendue"} le {fmtDateTime(company.suspended_at)}{company.suspend_reason ? ` — Motif : ${company.suspend_reason}` : ""}. Les accès sont bloqués, les données conservées.
        </div>
      )}

      <div className="mb-6 flex items-center gap-2 rounded-sm border border-border bg-muted px-4 py-3">
        <code data-testid="company-portal-url" className="min-w-0 flex-1 truncate text-sm font-semibold text-primary">{company.portal_url}</code>
        <button data-testid="copy-portal-url-button" className="text-muted-foreground hover:text-primary transition-colors" title="Copier le lien"
          onClick={() => { navigator.clipboard.writeText(company.portal_url); toast.success("Lien copié"); }}>
          <Copy className="h-4 w-4" />
        </button>
        <a data-testid="open-portal-button" href={company.portal_url} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-primary transition-colors" title="Ouvrir le portail">
          <ExternalLink className="h-4 w-4" />
        </a>
      </div>

      <Tabs defaultValue="infos" data-testid="company-tabs">
        <TabsList className="rounded-sm">
          <TabsTrigger value="infos" data-testid="tab-infos" className="rounded-sm">Informations</TabsTrigger>
          <TabsTrigger value="option-ia" data-testid="tab-option-ia" className="rounded-sm">Option IA</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users" className="rounded-sm">Utilisateurs ({company.users?.length || 0})</TabsTrigger>
          <TabsTrigger value="clients" data-testid="tab-clients" className="rounded-sm">Clients entreprise ({company.clients?.length || 0})</TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-categories" className="rounded-sm">Catégories ({tabData.categories.length})</TabsTrigger>
          <TabsTrigger value="tickets" data-testid="tab-tickets" className="rounded-sm">Tickets ({tabData.tickets.length})</TabsTrigger>
          <TabsTrigger value="emails" data-testid="tab-emails" className="rounded-sm">Emails simulés ({tabData.emails.length})</TabsTrigger>
          <TabsTrigger value="logs" data-testid="tab-logs" className="rounded-sm">Logs ({tabData.logs.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="infos" className="mt-6">
          <form onSubmit={save} className="rounded-sm border border-border bg-card p-6" data-testid="company-edit-form">
            <h2 className="mb-5 text-lg font-medium">Informations générales</h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div className="space-y-2"><Label>Nom société</Label><Input data-testid="edit-company-name" value={form.name} onChange={set("name")} className="rounded-sm" /></div>
              <div className="space-y-2"><Label>Slug portail</Label><Input data-testid="edit-company-slug" value={form.slug} onChange={set("slug")} className="rounded-sm font-mono" /></div>
              <div className="space-y-2"><Label>Email contact</Label><Input data-testid="edit-company-email" type="email" value={form.email} onChange={set("email")} className="rounded-sm" /></div>
              <div className="space-y-2"><Label>Téléphone</Label><Input data-testid="edit-company-phone" value={form.phone} onChange={set("phone")} className="rounded-sm" /></div>
              <div className="space-y-2"><Label>Site web</Label><Input data-testid="edit-company-website" value={form.website} onChange={set("website")} className="rounded-sm" /></div>
              <div className="space-y-2"><Label>Formule</Label>
                <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}>
                  <SelectTrigger data-testid="edit-company-plan" className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="decouverte">Découverte</SelectItem>
                    <SelectItem value="croissance">Croissance</SelectItem>
                    <SelectItem value="sur_mesure">Sur Mesure</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Adresse</Label><Input data-testid="edit-company-address" value={form.address} onChange={set("address")} className="rounded-sm" /></div>
              <div className="space-y-2"><Label>Code postal</Label><Input data-testid="edit-company-zip" value={form.zip_code} onChange={set("zip_code")} className="rounded-sm" /></div>
              <div className="space-y-2"><Label>Ville</Label><Input data-testid="edit-company-city" value={form.city} onChange={set("city")} className="rounded-sm" /></div>
              <div className="space-y-2"><Label>Pays</Label><Input data-testid="edit-company-country" value={form.country} onChange={set("country")} className="rounded-sm" /></div>
            </div>

            <h2 className="mb-5 mt-8 text-lg font-medium">Informations légales</h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div className="space-y-2"><Label>SIRET</Label><Input data-testid="edit-company-siret" value={form.siret} onChange={set("siret")} className="rounded-sm" /></div>
              <div className="space-y-2"><Label>SIREN</Label><Input data-testid="edit-company-siren" value={form.siren} onChange={set("siren")} className="rounded-sm" /></div>
              <div className="space-y-2"><Label>RCS</Label><Input data-testid="edit-company-rcs" value={form.rcs} onChange={set("rcs")} className="rounded-sm" /></div>
              <div className="space-y-2"><Label>Forme juridique</Label><Input data-testid="edit-company-legal-form" value={form.legal_form} onChange={set("legal_form")} className="rounded-sm" /></div>
              <div className="space-y-2"><Label>TVA intracommunautaire</Label><Input data-testid="edit-company-vat" value={form.vat_number} onChange={set("vat_number")} className="rounded-sm" /></div>
            </div>

            <h2 className="mb-5 mt-8 text-lg font-medium">Responsable principal</h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div className="space-y-2"><Label>Prénom</Label><Input data-testid="edit-contact-first-name" value={form.contact_first_name} onChange={set("contact_first_name")} className="rounded-sm" /></div>
              <div className="space-y-2"><Label>Nom</Label><Input data-testid="edit-contact-last-name" value={form.contact_last_name} onChange={set("contact_last_name")} className="rounded-sm" /></div>
              <div className="space-y-2"><Label>Fonction</Label><Input data-testid="edit-contact-role" value={form.contact_role} onChange={set("contact_role")} className="rounded-sm" /></div>
              <div className="space-y-2"><Label>Email</Label><Input data-testid="edit-contact-email" type="email" value={form.contact_email} onChange={set("contact_email")} className="rounded-sm" /></div>
              <div className="space-y-2"><Label>Téléphone</Label><Input data-testid="edit-contact-phone" value={form.contact_phone} onChange={set("contact_phone")} className="rounded-sm" /></div>
            </div>

            <div className="mt-8">
              <Button data-testid="save-company-button" type="submit" disabled={saving} className="rounded-sm bg-primary text-white hover:bg-red-700">
                <Save className="mr-2 h-4 w-4" /> {saving ? "Enregistrement…" : "Enregistrer les modifications"}
              </Button>
            </div>
          </form>
        </TabsContent>

        <TabsContent value="option-ia" className="mt-6">
          {(() => {
            const aiEff = company.ai_status_effective || company.ai_status || "disabled";
            return (
          <div className="rounded-sm border border-border bg-card p-6" data-testid="ai-option-panel">
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
              <h2 className="flex items-center gap-2 text-lg font-medium"><Sparkles className="h-4 w-4 text-primary" /> Option IA — Assistant Suivio360</h2>
              <span data-testid="ai-status-badge" className={`rounded-sm px-2 py-0.5 text-xs font-semibold ${AI_STATUS_STYLES[aiEff] || AI_STATUS_STYLES.disabled}`}>
                {AI_STATUS_LABELS[aiEff] || aiEff}
              </span>
            </div>
            <p className="mb-6 text-sm text-muted-foreground">
              Assistant IA (OpenAI) : reformulation de réponses, résumé de ticket, synthèse de clôture, suggestion de catégorie et priorité.
              L'IA propose, l'équipe valide — aucune action n'est jamais effectuée automatiquement.
            </p>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-sm border border-border p-3">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Début d'essai</div>
                <div className="mt-1 text-sm font-medium" data-testid="ai-trial-start">{fmtDateTime(company.ai_trial_start)}</div>
              </div>
              <div className="rounded-sm border border-border p-3">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Fin d'essai</div>
                <div className="mt-1 text-sm font-medium" data-testid="ai-trial-end">{fmtDateTime(company.ai_trial_end)}</div>
              </div>
              <div className="rounded-sm border border-border p-3">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Activée le</div>
                <div className="mt-1 text-sm font-medium" data-testid="ai-activated-at">{fmtDateTime(company.ai_activated_at)}</div>
              </div>
              <div className="rounded-sm border border-border p-3">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Désactivée le</div>
                <div className="mt-1 text-sm font-medium" data-testid="ai-deactivated-at">{fmtDateTime(company.ai_deactivated_at)}</div>
              </div>
            </div>
            <div className="mt-6 space-y-2">
              <Label>Notes internes (Super Admin uniquement)</Label>
              <Textarea data-testid="ai-notes-input" rows={3} value={aiNotes} onChange={(e) => setAiNotes(e.target.value)}
                className="rounded-sm" placeholder="Ex : essai demandé lors de la démo du…, relance commerciale prévue le…" />
              <Button type="button" variant="outline" size="sm" data-testid="ai-notes-save-button" className="rounded-sm"
                disabled={aiBusy} onClick={() => saveAiOption({ ai_notes: aiNotes }, "Notes enregistrées")}>
                Enregistrer les notes
              </Button>
            </div>
            <div className="mt-6 grid gap-4 border-t border-border pt-6 sm:grid-cols-2">
              <div className="rounded-sm border border-border p-5" data-testid="ai-permanent-card">
                <h3 className="text-sm font-semibold">Activation permanente</h3>
                <p className="mt-1 text-xs text-muted-foreground">
                  Active l'Assistant IA pour cette entreprise sans date de fin. L'option reste active tant qu'un Super Admin ne la désactive pas.
                </p>
                {aiEff === "active" ? (
                  <div className="mt-4 space-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`rounded-sm px-2 py-0.5 text-xs font-semibold ${AI_STATUS_STYLES.active}`}>Active</span>
                      <span className="text-xs text-muted-foreground">depuis le {fmtDateTime(company.ai_activated_at)}</span>
                    </div>
                    <p className="text-xs font-medium text-green-700 dark:text-green-400" data-testid="ai-active-note">
                      Option IA active sans date de fin.
                    </p>
                    <Button type="button" variant="outline" data-testid="ai-disable-button" disabled={aiBusy}
                      className="rounded-sm border-primary text-primary hover:bg-primary hover:text-white"
                      onClick={() => saveAiOption({ status: "disabled" }, "Option IA désactivée")}>
                      <Ban className="mr-2 h-4 w-4" /> Désactiver l'IA
                    </Button>
                  </div>
                ) : (
                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <Button type="button" data-testid="ai-activate-button" disabled={aiBusy}
                      className="rounded-sm bg-primary text-white hover:bg-red-700"
                      onClick={() => saveAiOption({ status: "active" }, "Option IA activée sans expiration")}>
                      {aiBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle className="mr-2 h-4 w-4" />}
                      Activer l'IA sans expiration
                    </Button>
                    {aiEff === "trial" && (
                      <Button type="button" variant="outline" data-testid="ai-disable-button" disabled={aiBusy}
                        className="rounded-sm border-primary text-primary hover:bg-primary hover:text-white"
                        onClick={() => saveAiOption({ status: "disabled" }, "Option IA désactivée")}>
                        <Ban className="mr-2 h-4 w-4" /> Désactiver l'IA
                      </Button>
                    )}
                  </div>
                )}
              </div>

              <div className="rounded-sm border border-border p-5" data-testid="ai-trial-card">
                <h3 className="text-sm font-semibold">Essai gratuit</h3>
                <p className="mt-1 text-xs text-muted-foreground">
                  Permet d'activer l'Assistant IA pour une durée limitée.
                </p>
                {aiEff === "trial" && (
                  <p className="mt-3 text-xs font-medium text-amber-700 dark:text-amber-400" data-testid="ai-trial-current">
                    Essai en cours jusqu'au {fmtDateTime(company.ai_trial_end)}.
                  </p>
                )}
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <Select value={aiTrialDays} onValueChange={setAiTrialDays}>
                    <SelectTrigger data-testid="ai-trial-days-select" className="w-44 rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">7 jours</SelectItem>
                      <SelectItem value="14">14 jours</SelectItem>
                      <SelectItem value="30">30 jours</SelectItem>
                      <SelectItem value="custom">Date personnalisée</SelectItem>
                    </SelectContent>
                  </Select>
                  {aiTrialDays === "custom" && (
                    <Input type="date" data-testid="ai-trial-date-input" value={aiTrialDate}
                      onChange={(e) => setAiTrialDate(e.target.value)} className="w-44 rounded-sm" />
                  )}
                  <Button type="button" variant="outline" data-testid="ai-trial-button" className="rounded-sm"
                    disabled={aiBusy || (aiTrialDays === "custom" && !aiTrialDate)}
                    onClick={() => saveAiOption(
                      aiTrialDays === "custom" ? { status: "trial", trial_end: aiTrialDate } : { status: "trial", trial_days: Number(aiTrialDays) },
                      aiEff === "trial" ? "Essai prolongé" : "Essai gratuit lancé")}>
                    {aiEff === "trial" ? "Prolonger l'essai" : "Lancer un essai gratuit"}
                  </Button>
                </div>
                {aiEff === "expired" && (
                  <p className="mt-3 text-xs text-muted-foreground" data-testid="ai-expired-note">
                    L'essai gratuit est expiré — les boutons IA sont à nouveau verrouillés pour cette entreprise. Activez l'option ou relancez un essai.
                  </p>
                )}
              </div>
            </div>
            <p className="mt-4 text-xs text-muted-foreground">
              Sans option active ou sans clé OpenAI configurée sur l'environnement, aucun appel IA n'est effectué.
            </p>
          </div>
            );
          })()}
        </TabsContent>

        <TabsContent value="users" className="mt-6">
          <div className="rounded-sm border border-border bg-card">
            {(company.users || []).map((u) => (
              <div key={u.id} className="flex items-center justify-between gap-3 border-b border-border px-5 py-3 last:border-b-0" data-testid={`company-user-${u.email}`}>
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">{u.first_name} {u.last_name}</div>
                  <div className="truncate text-xs text-muted-foreground">{u.email}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground">{u.status === "active" ? "Actif" : "Désactivé"}</span>
                  <RoleBadge role={u.role} />
                </div>
              </div>
            ))}
            {!(company.users || []).length && <div className="p-6 text-sm text-muted-foreground">Aucun utilisateur.</div>}
          </div>
        </TabsContent>

        <TabsContent value="clients" className="mt-6">
          <div className="rounded-sm border border-border bg-card">
            {(company.clients || []).map((c) => (
              <Link key={c.id} to={`/app/clients/${c.id}`} data-testid={`company-client-${c.id}`}
                className="flex items-center justify-between gap-3 border-b border-border px-5 py-3 last:border-b-0 hover:bg-accent transition-colors">
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">{c.name}</div>
                  <div className="truncate text-xs text-muted-foreground">{c.contact_name} · {c.contact_email}</div>
                </div>
                <span className="text-xs text-muted-foreground">{PORTAL_LABELS[c.portal_access?.status] || "Non créé"}</span>
              </Link>
            ))}
            {!(company.clients || []).length && <div className="p-6 text-sm text-muted-foreground">Aucun client.</div>}
          </div>
        </TabsContent>

        <TabsContent value="categories" className="mt-6">
          <div className="rounded-sm border border-border bg-card">
            {tabData.categories.map((c) => (
              <div key={c.id} className="flex items-center justify-between gap-3 border-b border-border px-5 py-3 last:border-b-0" data-testid={`company-category-${c.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: c.color }} />
                  <span className="truncate text-sm font-medium">{c.name}</span>
                </div>
                <span className="text-xs text-muted-foreground">{c.status === "active" ? "Active" : c.status === "inactive" ? "Inactive" : "Archivée"}</span>
              </div>
            ))}
            {!tabData.categories.length && <div className="p-6 text-sm text-muted-foreground">Aucune catégorie.</div>}
          </div>
        </TabsContent>

        <TabsContent value="tickets" className="mt-6">
          <div className="rounded-sm border border-border bg-card">
            {tabData.tickets.map((t) => (
              <Link key={t.id} to={`/app/tickets/${t.id}`} data-testid={`company-ticket-${t.number}`}
                className="flex flex-wrap items-center gap-3 border-b border-border px-5 py-3 last:border-b-0 hover:bg-accent transition-colors">
                <span className="font-mono text-xs text-muted-foreground">{t.number}</span>
                <span className="min-w-0 flex-1 truncate text-sm font-medium">{t.subject}</span>
                <span className="text-xs text-muted-foreground">{t.client_name}</span>
                <StatusBadge status={t.status} />
                <PriorityBadge priority={t.priority} />
                <span className="text-xs text-muted-foreground">{fmtDateTime(t.created_at)}</span>
              </Link>
            ))}
            {!tabData.tickets.length && <div className="p-6 text-sm text-muted-foreground">Aucun ticket.</div>}
          </div>
        </TabsContent>

        <TabsContent value="emails" className="mt-6">
          <div className="rounded-sm border border-border bg-card">
            {tabData.emails.map((e) => (
              <div key={e.id} className="flex flex-wrap items-center gap-3 border-b border-border px-5 py-3 last:border-b-0" data-testid={`company-email-${e.id}`}>
                <code className="text-xs font-semibold">{e.template}</code>
                <span className="min-w-0 flex-1 truncate text-sm">{e.subject}</span>
                <span className="text-xs text-muted-foreground">→ {e.recipient_email}</span>
                <span className="rounded-sm bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-900 dark:bg-amber-950 dark:text-amber-300">SIMULÉ</span>
                <span className="text-xs text-muted-foreground">{fmtDateTime(e.created_at)}</span>
              </div>
            ))}
            {!tabData.emails.length && <div className="p-6 text-sm text-muted-foreground">Aucun email.</div>}
          </div>
        </TabsContent>

        <TabsContent value="logs" className="mt-6">
          <div className="rounded-sm border border-border bg-card">
            {tabData.logs.map((l) => (
              <div key={l.id} className="flex flex-wrap items-center gap-3 border-b border-border px-5 py-3 last:border-b-0" data-testid={`company-log-${l.id}`}>
                <span className="whitespace-nowrap text-xs text-muted-foreground">{fmtDateTime(l.created_at)}</span>
                <span className="text-sm font-medium">{l.action}</span>
                <span className="text-xs text-muted-foreground">{l.actor_name}</span>
                {Object.keys(l.detail || {}).length > 0 && <code className="max-w-96 truncate text-xs text-muted-foreground">{JSON.stringify(l.detail)}</code>}
              </div>
            ))}
            {!tabData.logs.length && <div className="p-6 text-sm text-muted-foreground">Aucun log.</div>}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={suspendOpen} onOpenChange={setSuspendOpen}>
        <DialogContent className="rounded-sm" data-testid="suspend-dialog">
          <DialogHeader>
            <DialogTitle>Suspendre {company.name} ?</DialogTitle>
            <DialogDescription>
              Les admins, collaborateurs et clients entreprise perdront l'accès immédiatement. Les données restent conservées et consultables par le Super Admin.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Motif (optionnel)</Label>
            <Input data-testid="suspend-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} className="rounded-sm" placeholder="Ex : impayé, pause contrat…" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSuspendOpen(false)} className="rounded-sm">Annuler</Button>
            <Button data-testid="confirm-suspend-button" onClick={() => toggleStatus("suspended")} className="rounded-sm bg-primary text-white hover:bg-red-700">Suspendre</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={archiveOpen} onOpenChange={setArchiveOpen}
        title={`Archiver ${company.name} ?`}
        description="La société et ses accès seront désactivés. Les données restent conservées et la société peut être réactivée. Action journalisée."
        confirmLabel="Archiver" onConfirm={() => toggleStatus("archived")} testid="archive-company-dialog" />
    </div>
  );
}
